import { useRef, useState } from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ClearIcon from '@mui/icons-material/Clear'
import PrintIcon from '@mui/icons-material/Print'
import SearchIcon from '@mui/icons-material/Search'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { PickEntries, PickItem, ProductId, pickOrderBreakdown, shortfallBreakdown } from '../picking'
import { matchesProductSearch } from '../productIdentity'
import QuantityField from './QuantityField'

export type PickStepProps = {
	/** Every product the run covers, grouped and in bin order by `pickItems`. */
	items: PickItem[],
	entries: PickEntries,
	/** Null clears the product back to blank. */
	onEntryChange: ( productId: ProductId, quantity: number | null ) => void,
	onPrintPickList: () => void,
}

const MONOSPACE = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace'

/**
 * The list's columns on a wide screen, shared by the header and every row so
 * they line up. The last is fixed: field, "of 9999" and the check always fit.
 */
const ROW_COLUMNS = { xs: '1fr', md: '160px minmax(0, 1fr) 272px' }

/**
 * A row's own label for a column, shown only when the rows stack on a narrow
 * screen and the header row is gone. On a wide screen the header names the
 * column once instead of every row repeating it.
 */
function StackedLabel( { children }: { children: string } ) {
	return (
		<Typography
			variant="caption"
			color="text.secondary"
			aria-hidden
			sx={{ display: { xs: 'block', md: 'none' }, fontWeight: 500, mb: 0.25 }}
		>
			{children}
		</Typography>
	)
}

/** Names the columns once, on a wide screen. */
function ListHeader() {
	return (
		<Box
			aria-hidden
			sx={{
				display: { xs: 'none', md: 'grid' },
				gridTemplateColumns: ROW_COLUMNS,
				columnGap: 2,
				px: 2,
				py: 1,
				borderBottom: 1,
				borderColor: 'pickFlow.cardBorder',
				bgcolor: 'grey.50',
				typography: 'body2',
				fontWeight: 600,
				color: 'text.secondary',
			}}
		>
			<span>Bin</span>
			<span>Product</span>
			<span>Quantity</span>
		</Box>
	)
}

type PickRowProps = {
	item: PickItem,
	entry: number | null,
	isLast: boolean,
	onChange: ( quantity: number | null ) => void,
	onEnter: () => void,
	inputRef: ( element: HTMLInputElement | null ) => void,
}

/**
 * One product: where it is, what it is, how many, and the count.
 *
 * Side by side on a wide screen, stacked on a narrow one — the bin and the part
 * number stay first either way, because they are what the picker is matching.
 */
function PickRow( { item, entry, isLast, onChange, onEnter, inputRef }: PickRowProps ) {
	const theme = useTheme()
	const { sku, madeBy, description } = item.identity
	const splitsOrders = item.lines.length > 1
	const short = entry === null ? 0 : item.toPick - entry
	const isFull = entry !== null && short === 0

	return (
		<Box
			sx={{
				display: 'grid',
				gridTemplateColumns: ROW_COLUMNS,
				columnGap: 2,
				rowGap: 1.5,
				alignItems: 'center',
				px: 2,
				py: 1.5,
				borderBottom: isLast ? 0 : 1,
				borderColor: 'pickFlow.cardBorder',
				bgcolor: isFull ? 'pickFlow.stepCompleteBackground' : 'transparent',
			}}
		>
			<Box>
				<StackedLabel>Bin</StackedLabel>
				<Typography
					variant="h6"
					sx={{
						fontFamily: MONOSPACE,
						color: item.binLocation ? 'text.primary' : 'text.secondary',
						wordBreak: 'break-word',
						lineHeight: 1.3,
					}}
				>
					{item.binLocation ?? 'No bin'}
				</Typography>
			</Box>

			<Box sx={{ minWidth: 0 }}>
				<Typography variant="body1" sx={{ fontFamily: MONOSPACE, fontWeight: 700, wordBreak: 'break-all' }}>
					{sku ?? 'No part number'}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					{[ madeBy, description ].filter( Boolean ).join( ' · ' )}
				</Typography>
				{short > 0 && (
					<Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
						{short} short{splitsOrders ? ` (${shortfallBreakdown( item, entry ?? 0 )})` : ''}
					</Typography>
				)}
				{splitsOrders && (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						{entry !== null && entry > 0
							? `Goes to ${pickOrderBreakdown( item, entry )}`
							: `For ${item.lines.map( ( claim ) => `FO-${claim.line.fulfillmentOrder.orderNumber} ×${claim.toPick}` ).join( ' · ' )}`}
					</Typography>
				)}
			</Box>

			<Box>
				<StackedLabel>Quantity</StackedLabel>
				<Stack direction="row" spacing={1.5} alignItems="center" sx={{ minHeight: theme.touch.control }}>
					<QuantityField
						label={sku ? `${sku} ${description}` : description}
						value={entry}
						min={0}
						max={item.toPick}
						onChange={onChange}
						onEnter={onEnter}
						inputRef={inputRef}
					/>
					{/* A fixed width, wide enough for "of 9999", so the fields line up down
				    the list however many digits each product asks for. */}
					<Typography
						variant="body1"
						color="text.secondary"
						sx={{ whiteSpace: 'nowrap', width: '7ch', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
					>
					of {item.toPick}
					</Typography>
					<CheckCircleIcon
						color="success"
						aria-label={isFull ? 'Picked in full' : undefined}
						aria-hidden={!isFull}
						sx={{ visibility: isFull ? 'visible' : 'hidden' }}
					/>
				</Stack>
			</Box>
		</Box>
	)
}

/**
 * Every product on the run at once, in bin order, each with a blank count.
 *
 * The picker fills in what they found, in any order. The search narrows the
 * list by part number, manufacturer or description for a long run. Enter (or
 * Done on the pad) moves to the next product, so a keyboard walks the list
 * without a mouse. Nothing is recorded until they continue to pack; a blank is
 * a zero then.
 */
export default function PickStep( { items, entries, onEntryChange, onPrintPickList }: PickStepProps ) {
	const [ query, setQuery ] = useState( '' )
	const inputs = useRef( new Map<ProductId, HTMLInputElement>() )

	if ( items.length === 0 ) {
		return <Alert severity="info">Nothing left to pick.</Alert>
	}

	const shown = items.filter( ( item ) => matchesProductSearch( item.identity, query ) )

	const focusAfter = ( index: number ) => {
		const next = shown[ index + 1 ]
		if ( next ) {
			inputs.current.get( next.productId )?.focus()
		}
	}

	return (
		<Stack spacing={2}>
			<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
				<TextField
					value={query}
					onChange={( event ) => setQuery( event.target.value )}
					placeholder="Search by part number, manufacturer or description"
					size="medium"
					fullWidth
					slotProps={{
						htmlInput: { 'aria-label': 'Search products' },
						input: {
							startAdornment: (
								<InputAdornment position="start"><SearchIcon /></InputAdornment>
							),
							endAdornment: query ? (
								<InputAdornment position="end">
									<IconButton aria-label="Clear search" onClick={() => setQuery( '' )} edge="end">
										<ClearIcon />
									</IconButton>
								</InputAdornment>
							) : undefined,
							sx: { bgcolor: 'pickFlow.surface' },
						},
					}}
				/>
				<Button
					variant="outlined"
					startIcon={<PrintIcon />}
					onClick={onPrintPickList}
					sx={{ flexShrink: 0, minHeight: 48, whiteSpace: 'nowrap' }}
				>
					Print pick list
				</Button>
			</Stack>

			{query && (
				<Typography variant="body2" color="text.secondary">
					Showing {shown.length} of {items.length} products
				</Typography>
			)}

			<Paper variant="outlined" sx={{ borderColor: 'pickFlow.cardBorder', bgcolor: 'pickFlow.surface', overflow: 'hidden' }}>
				<ListHeader />
				{shown.length === 0 ? (
					<Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
						No products match “{query}”.
					</Typography>
				) : shown.map( ( item, index ) => (
					<PickRow
						key={item.productId}
						item={item}
						entry={entries[ item.productId ] ?? null}
						isLast={index === shown.length - 1}
						onChange={( quantity ) => onEntryChange( item.productId, quantity )}
						onEnter={() => focusAfter( index )}
						inputRef={( element ) => {
							if ( element ) {
								inputs.current.set( item.productId, element )
							} else {
								inputs.current.delete( item.productId )
							}
						}}
					/>
				) )}
			</Paper>
		</Stack>
	)
}
